import React, { useEffect, useRef } from "react";
import { prepareAudiosToManifest } from "../../utils/PageManifestParsing/audioParsing";
import { TimelineDrawer } from "../TimelineDrawer";
import { isTimelineEnabled } from "../../utils/Lesson";
import { useLessonPage, useLessonPagesState } from "../../contexts/LessonPagesProvider/LessonPagesProvider";
import { useAudioManagerStore } from "../../contexts/PageAudioManager";
import { useObjectsUtils } from "../../contexts/ObjectsProvider";
import { useNarrationStore } from "../../panels/TimelinePanel/Right/CompositeNarrationRow/store";
import { useShallow } from "zustand/react/shallow";
import { EffectAudio, NarrationAudio } from "../../models/IInteractiveAudio";
import { audioBlobToApi } from "../../panels/TimelinePanel/helpers";
import { getSortedAudios } from "../../contexts/PageAudioManager/helpers";
import { useAudioEffectStore } from "../../panels/TimelinePanel/Right/AudioEffectRow/store";

export const DrawerContainer = () => {
  const lessonPagesState = useLessonPagesState();
  const isInitialLoad = useRef(true);
  const [loadNarrationAudio, loadEffectAudio, resetNarration, resetEffect, isDirty, setIsDirty] = useAudioManagerStore(
    useShallow((state) => [
      state.loadNarrationAudio,
      state.loadEffectAudio,
      state.resetNarration,
      state.resetEffects,
      state.isDirty,
      state.setIsDirty,
    ]),
  );
  const narrationAudios = useAudioManagerStore((state) => state.narrationAudios);
  const audioEffects = useAudioManagerStore((state) => state.audioEffects);
  const { pageManifest, updateManifest, currentLessonPage, currentLessonPageIndex } = useLessonPage();
  const { needtoSave } = useObjectsUtils();
  const { setObjects } = useNarrationStore();
  const { setObjects: setAudioEffects } = useAudioEffectStore();

  const hasTimeline = isTimelineEnabled(lessonPagesState.currentLessonPage);

  // Load audio from manifest when page changes
  useEffect(() => {
    isInitialLoad.current = true;

    resetNarration();
    resetEffect();

    const audiosNarrationToLoad = getSortedAudios([...(pageManifest?.narrationAudios ?? [])]) as NarrationAudio[];

    for (const audio of audiosNarrationToLoad) {
      loadNarrationAudio(audio.objectId, audioBlobToApi(audio.input as string), {
        ...audio,
      });
    }

    const audiosEffectsToLoad = getSortedAudios([...(pageManifest?.audioEffects ?? [])]) as EffectAudio[];

    for (const audio of audiosEffectsToLoad) {
      loadEffectAudio(audio.objectId, audioBlobToApi(audio.input as string), {
        ...audio,
      });
    }

    // Allow manifest sync after initial load settles
    requestAnimationFrame(() => {
      isInitialLoad.current = false;
    });
  }, [currentLessonPage?.pageId, currentLessonPageIndex]);

  // Save
  useEffect(() => {
    if (isDirty) {
      needtoSave();
      setIsDirty(false);
    }
  }, [isDirty]);

  // Sync narration audio to manifest (skip during initial page load)
  useEffect(() => {
    if (isInitialLoad.current) return;

    setObjects(narrationAudios);
    updateManifest({
      audios: [],
      narrationAudios: prepareAudiosToManifest(narrationAudios),
    });
  }, [narrationAudios]);

  // Sync audio effects to manifest (skip during initial page load)
  useEffect(() => {
    if (isInitialLoad.current) return;

    setAudioEffects(audioEffects);
    updateManifest({
      audioEffects: prepareAudiosToManifest(audioEffects),
    });
  }, [audioEffects]);

  return <>{hasTimeline && <TimelineDrawer />}</>;
};
